import {
  ControlOption,
  OxyNestedSelector,
  OxySelector,
  OxySelectorV
} from "@/oxygen/classes/lib/types";
import { v4 as uuidv4 } from "uuid";
import { documentStore, globalStore, uiStore } from "@/store/store-accessor";
import { findNode, findNodeParent } from "@/lib/tree/tree";
import { PropertiesData, TreeNode } from "@/types/tree";
import { ClassProperties } from "@/oxygen/classes/lib/type-properties";
import { breakdanceUndo } from "@/lib/undo/undo";
import defensiveCopy from "@/util/defensive-copy";
import { BASE_BREAKPOINT_ID } from "@/lib/breakpoints/breakpoints";
import { writeToLocalStorage } from "@/util/localstorage";
import { pipe } from "fp-ts/function";
import { fpJsonStringify } from "@/util/fp-json-stringify";
import { fold } from "fp-ts/Either";
import fpJsonParseTypesafe from "@/util/fp-json-parse-typesafe";
import * as t from "io-ts";
import { isEqual } from "lodash";

export const DEFAULT_NEW_COLLECTION = "Collection";

export const DEFAULT_SELECTORS_COLLECTION = "Default";

export const AVAILABLE_STATES = [
  "&:hover",
  "&:active",
  "&:focus",
  "&:focus-within",
  "&:focus-visible"
];

const stripDot = (str: string) =>
  str.startsWith(".") ? str.substring(1) : str;

export function createSelectorFromName(
  name: string,
  properties?: Record<string, ClassProperties> | null,
  type: "class" | "custom" = "class",
  collection = DEFAULT_SELECTORS_COLLECTION
): OxySelector {
  return {
    id: uuidv4(),
    name: type === "class" ? stripDot(name) : name,
    properties,
    children: [],
    locked: false,
    collection,
    type
  };
}

export function createNestedSelectorFromName(
  name: string,
  properties?: Record<string, ClassProperties> | null
): OxyNestedSelector {
  return {
    id: uuidv4(),
    name,
    properties,
    locked: false
  };
}

export function isDuplicateSelector(name: string, ignoreSelectorId?: string) {
  const selectors = ignoreSelectorId
    ? globalStore.oxySelectors.filter(s => s.id !== ignoreSelectorId)
    : globalStore.oxySelectors;

  return selectors.some(s => s.name === name);
}

export function isDuplicateNestedSelector(
  name: string,
  parentId: string,
  ignoreSelectorId?: string
) {
  const parent = globalStore.oxySelectors.find(s => s.id === parentId);

  if (!parent) return false;

  const children = ignoreSelectorId
    ? parent.children?.filter(c => c.id !== ignoreSelectorId)
    : parent.children;

  return children?.some(c => c.name === name);
}

// TODO: This function only takes into account the base breakpoint. It should be updated to take into account all breakpoints.
export function findLastParentClassThatHasDisplay(displays: string[]) {
  return () => {
    if (!uiStore.activeElement) return false;

    const currentBreakpoint = BASE_BREAKPOINT_ID;

    const parent = findNodeParent(
      documentStore.document.tree,
      uiStore.activeElement
    );

    if (!parent) return false;

    const selectors = getElementOxySelectors(parent);

    const lastSelectorWithDisplay = selectors.reverse().find(s => {
      const layout = s?.properties?.[currentBreakpoint]?.layout;
      return layout?.display !== undefined && layout?.display !== null;
    });

    const lastDisplay =
      lastSelectorWithDisplay?.properties?.[currentBreakpoint]?.layout?.display;

    if (!lastSelectorWithDisplay || !lastDisplay) {
      return false;
    }

    return displays.includes(lastDisplay);
  };
}

export function setElementSelectors(
  elementId: TreeNode["id"],
  classes: string[]
) {
  documentStore.unthrottledPropertyChanged({
    elementId: elementId,
    path: "meta.classes",
    meta: {
      snapshotLabel: `Add class to element`
    },
    value: classes
  });
}

export function removeElementSelector(
  elementId: TreeNode["id"],
  idToRemove: string
) {
  const node = findNode(documentStore.document.tree, elementId);
  if (!node) return;

  const ids: string[] = node?.data?.properties?.meta?.classes || [];
  const newSelectors = ids.filter(id => id !== idToRemove);

  setElementSelectors(elementId, newSelectors);
}

export function getElementOxySelectors(node: TreeNode): OxySelector[] {
  const ids = node?.data?.properties?.meta?.classes || [];
  return ids
    .map((id: string) => globalStore.oxySelectors.find(s => s.id === id))
    .filter(Boolean) as OxySelector[];
}

export function addSelectorToStore(selector: OxySelector) {
  breakdanceUndo.transaction(() => {
    // Find the index of the last item with the same collection
    const selectors = globalStore.oxySelectors;
    let insertIndex = selectors.length;

    for (let i = selectors.length - 1; i >= 0; i--) {
      if (selectors[i].collection === selector.collection) {
        insertIndex = i + 1;
        break;
      }
    }

    const newSelectors = [
      ...selectors.slice(0, insertIndex),
      selector,
      ...selectors.slice(insertIndex)
    ];

    globalStore.setOxygenSelectors(newSelectors);
  }, "Add Selector");
}

export function removeSelectorFromStore(id: string) {
  breakdanceUndo.transaction(() => {
    globalStore.setOxygenSelectors(
      globalStore.oxySelectors.filter(s => s.id !== id)
    );
  }, "Remove Selector");
}

export function renameSelector(id: string, name: string) {
  breakdanceUndo.transaction(() => {
    globalStore.setOxygenSelectors(
      globalStore.oxySelectors.map(s => {
        if (s.id === id) {
          return {
            ...s,
            name: s.type === "class" ? stripDot(name) : name
          };
        }

        return s;
      })
    );
  }, "Rename Selector");
}

function attemptToNameCopy(name: string) {
  const copyPattern = /-copy-\d+$/;
  if (copyPattern.test(name)) {
    const copyNumber = parseInt(name.match(/\d+$/)![0]);
    const cleanName = name.replace(copyPattern, "").trim();
    return `${cleanName}-copy-${copyNumber + 1}`;
  }

  return `${name}-copy-1`;
}

export function duplicateSelector(id: string) {
  const selector = globalStore.oxySelectors.find(s => s.id === id);
  if (!selector) return;

  const newSelector = defensiveCopy(selector);
  newSelector.id = uuidv4();
  newSelector.name = attemptToNameCopy(selector.name);

  breakdanceUndo.transaction(() => {
    globalStore.setOxygenSelectors([...globalStore.oxySelectors, newSelector]);
  }, "Duplicate Selector");

  return newSelector;
}

export function addNestedSelector(
  parentId: string,
  selector: OxyNestedSelector
) {
  const index = globalStore.oxySelectors.findIndex(s => s.id === parentId);

  if (index === -1) return false;

  const selectors = defensiveCopy(globalStore.oxySelectors);
  const childAlreadyExists = selectors[index].children?.find(
    c => c.id === selector.id
  );

  if (childAlreadyExists) return false;

  if (!selectors[index].children) {
    selectors[index].children = [];
  }

  selectors[index].children!.push(selector);

  breakdanceUndo.transaction(() => {
    globalStore.setOxygenSelectors(selectors);
  }, "Add Nested Selector");

  return true;
}

export function removeNestedSelector(parentId: string, id: string) {
  const index = globalStore.oxySelectors.findIndex(s => s.id === parentId);

  if (index === -1) return false;

  const selectors = defensiveCopy(globalStore.oxySelectors);
  const nestedIndex = selectors[index].children?.findIndex(c => c.id === id);

  if (nestedIndex === -1 || nestedIndex === undefined) return false;

  selectors[index].children!.splice(nestedIndex, 1);

  breakdanceUndo.transaction(() => {
    globalStore.setOxygenSelectors(selectors);
  }, "Remove Nested Selector");

  return true;
}

export function renameNestedSelector(
  parentId: string,
  id: string,
  newName: string
) {
  const index = globalStore.oxySelectors.findIndex(s => s.id === parentId);

  if (index === -1) return false;

  const selectors = defensiveCopy(globalStore.oxySelectors);
  const nestedIndex = selectors[index].children?.findIndex(c => c.id === id);

  if (nestedIndex === -1 || nestedIndex === undefined) return false;

  selectors[index].children![nestedIndex].name = newName;

  breakdanceUndo.transaction(() => {
    globalStore.setOxygenSelectors(selectors);
  }, "Rename Nested Selector");

  return true;
}

export function duplicateNestedSelector(parentId: string, id: string) {
  const index = globalStore.oxySelectors.findIndex(s => s.id === parentId);

  if (index === -1) return;

  const selectors = defensiveCopy(globalStore.oxySelectors);
  const nestedIndex = selectors[index].children?.findIndex(c => c.id === id);

  if (nestedIndex === -1 || nestedIndex === undefined) return false;

  const newSelector = defensiveCopy(selectors[index].children![nestedIndex]);
  newSelector.id = uuidv4();
  newSelector.name = attemptToNameCopy(newSelector.name);

  if (!selectors[index].children) {
    selectors[index].children = [];
  }

  selectors[index].children!.push(newSelector);

  breakdanceUndo.transaction(() => {
    globalStore.setOxygenSelectors(selectors);
  }, "Duplicate Nested Selector");

  return newSelector;
}

export function updateSelectorProperties(change: {
  selectorId: string;
  nestedSelectorId?: string;
  properties: PropertiesData;
  transactionKey?: string;
}) {
  const selectors = defensiveCopy(globalStore.oxySelectors);
  const selectorIndex = selectors.findIndex(s => s.id === change.selectorId);
  const selector = selectors[selectorIndex];

  if (selectorIndex !== -1) {
    // If a nested selector is being changed, find it and update its properties
    if (change.nestedSelectorId) {
      const nestedIndex = selector.children?.findIndex(
        nested => nested.id === change.nestedSelectorId
      );

      if (!selector.children) {
        selector.children = [];
      }

      if (
        nestedIndex !== undefined &&
        nestedIndex !== -1 &&
        selector.children[nestedIndex]
      ) {
        selector.children[nestedIndex] = {
          ...selector.children[nestedIndex],
          properties: change.properties
        };
      }
    } else {
      // Otherwise, update the main selector's properties
      selector.properties = change.properties;
    }
  }

  breakdanceUndo.transaction(
    () => globalStore.setOxygenSelectors(selectors),
    "Update Selector Properties",
    change?.transactionKey
  );
}

export function toggleSelectorLock(id: string) {
  const selectors = defensiveCopy(globalStore.oxySelectors);
  const index = selectors.findIndex(s => s.id === id);

  if (index === -1) return;

  selectors[index].locked = !selectors[index].locked;

  breakdanceUndo.transaction(
    () => globalStore.setOxygenSelectors(selectors),
    "Toggle Selector Lock"
  );
}

export function toggleNestedSelectorLock(parentId: string, id: string) {
  const selectors = defensiveCopy(globalStore.oxySelectors);
  const index = selectors.findIndex(s => s.id === parentId);

  if (index === -1) return;

  const nestedIndex = selectors[index].children?.findIndex(c => c.id === id);

  if (nestedIndex === -1 || nestedIndex === undefined) return;

  selectors[index].children![nestedIndex].locked =
    !selectors[index].children![nestedIndex].locked;

  breakdanceUndo.transaction(
    () => globalStore.setOxygenSelectors(selectors),
    "Toggle Nested Selector Lock"
  );
}

export function mapOptionToDropdown(option: ControlOption) {
  if (typeof option === "string") {
    return {
      value: option,
      text: option
    };
  }

  return {
    value: option.value,
    text: option.label
  };
}

export function mapOptionToButtonBar(option: ControlOption) {
  if (typeof option === "string") {
    return {
      value: option,
      label: option
    };
  }

  return option;
}

export function saveSelectorsToLocalStorage() {
  // Only sync to localstorage if it has changed locally,
  // this prevents old versions from overriding latest ones.
  if (isEqual(globalStore.untouchedOxySelectors, globalStore.oxySelectors)) {
    return;
  }

  const selectorsJson = pipe(
    fpJsonStringify(globalStore.oxySelectors),
    fold(
      () => "",
      result => result
    )
  );

  writeToLocalStorage("oxy_selectors", selectorsJson);
}

export function updateSelectorsFromStorageEvent(event: StorageEvent) {
  if (!event.newValue) return;

  const newSelectors = pipe(
    fpJsonParseTypesafe(event.newValue, t.array(OxySelectorV)),
    fold(
      () => [],
      selectors => selectors
    )
  );

  if (!newSelectors.length) return;

  const currentSelectors = globalStore.oxySelectors;

  if (isEqual(currentSelectors, newSelectors)) return;

  breakdanceUndo.transaction(() => {
    const hasUnsavedChangesBeforeSettingSelectors =
      uiStore.unsavedChangesPresent;

    globalStore.setOxygenSelectors(newSelectors);
    globalStore.markCurrentOxySelectorsAsUntouched();

    if (!hasUnsavedChangesBeforeSettingSelectors) {
      // We disable the unsaved changes indicator because the selectors we are passing here are already on the database.
      uiStore.setUnsavedChangesPresent(false);
    }
  }, "Update Selectors between tabs");

  console.log("Selectors updated from storage", newSelectors);
}
